// Design System - Header Global Unifié Premium
// Mobile: Logo centré (accueil) ou compact (autres pages) + Recherche élargie
// Desktop: Structure classique [Logo] [Recherche] [CTA] [Compte]
// Menu Hamburger Premium avec slide-in depuis la droite
// Menu Hamburger Espace Pro pour les routes /pro/*
// IMMERSIVE SCROLL: Header disparaît au scroll down, réapparaît au scroll up

import { useState, useEffect, useRef } from 'react';
import { API_URL } from '../../config/api';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, User, ShoppingCart, Menu, X, Bell, Heart, 
  ChevronDown, LogOut, Settings, Package, FileText,
  Home, Users, Store, Megaphone, Network, Briefcase,
  Rocket, Crown, Sparkles, TrendingUp, MapPin, Star, 
  Clock, Building2, Bot, Eye, Target, Handshake, UserPlus,
  Shield, ChevronRight, Gift, Moon, Sun, CreditCard,
  Stethoscope, ClipboardList, BarChart3, Zap, Tag, ChevronUp,
  Plus, Loader2, SlidersHorizontal
} from 'lucide-react';
import { Button } from '../ui/button';
import { useTheme } from '../../context/ThemeContext';
import { useBuyer } from '../../contexts/BuyerContext';
import { PublishModal } from '../publish';
import NotificationCenter from '../notifications/NotificationCenter';
import { CreditBadge } from '../credits';
import { useImmersiveScrollContext } from '../../context/ImmersiveScrollContext';
import { useNavMode, NAV_MODES } from '../../context/NavModeContext';
import SmartSearchDropdown from '../search/SmartSearchDropdown';
import { 
  CATEGORIES_PARTICULIERS, 
  TYPES_ANNONCES,
  getAutocompleteSuggestions as getParticuliersSuggestions,
  ICONS_MAP as PARTICULIERS_ICONS_MAP
} from '../../data/categories-particuliers';
import { useDemoMode } from '../demo/DemoModeProvider';

// ═══ COMPOSANT MODE SELECTOR (Shop/Réseau/Pro) ═══
function ModeSelector({ className = '' }) {
  const { mode, setMode, isShopMode, isReseauMode, NAV_MODES } = useNavMode();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  
  const handleSelect = (selectedMode) => {
    if (selectedMode === 'pro') {
      // Pro ouvre directement l'espace pro
      navigate('/pro');
    } else {
      setMode(selectedMode);
      // Naviguer vers la page correspondante
      if (selectedMode === NAV_MODES.SHOP) {
        navigate('/');
      } else if (selectedMode === NAV_MODES.RESEAU) {
        navigate('/reseau');
      }
    }
    setIsOpen(false);
  };
  
  const currentLabel = isShopMode ? 'Shop' : 'Réseau';
  
  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg transition-colors"
        style={{ 
          backgroundColor: isShopMode ? 'rgba(249, 115, 22, 0.15)' : 'rgba(59, 130, 246, 0.15)',
          color: isShopMode ? '#F97316' : '#3B82F6',
        }}
        data-testid="mode-selector-btn"
      >
        <span className="text-xs font-bold">{currentLabel}</span>
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay */}
            <div 
              className="fixed inset-0 z-[200]" 
              onClick={() => setIsOpen(false)}
            />
            {/* Menu */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute top-full left-0 mt-1 w-36 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-[201]"
            >
              <button
                onClick={() => handleSelect(NAV_MODES.SHOP)}
                className={`w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors ${isShopMode ? 'bg-orange-50' : 'hover:bg-gray-50'}`}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isShopMode ? '#F97316' : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
                <span className={`text-sm font-medium ${isShopMode ? 'text-[#F97316]' : 'text-gray-700'}`}>Shop</span>
                {isShopMode && <span className="ml-auto text-[#F97316]">✓</span>}
              </button>
              <button
                onClick={() => handleSelect(NAV_MODES.RESEAU)}
                className={`w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors ${isReseauMode ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isReseauMode ? '#3B82F6' : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                <span className={`text-sm font-medium ${isReseauMode ? 'text-[#3B82F6]' : 'text-gray-700'}`}>Réseau</span>
                {isReseauMode && <span className="ml-auto text-[#3B82F6]">✓</span>}
              </button>
              <div className="border-t border-gray-100" />
              <button
                onClick={() => handleSelect('pro')}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                </svg>
                <span className="text-sm font-medium text-gray-700">Pro</span>
                <ChevronRight size={14} className="ml-auto text-gray-400" />
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// Configuration du menu Espace Pro pour mobile
const ESPACE_PRO_MOBILE_MENU = [
  { path: '/pro', label: 'Présentation', icon: FileText },
  { path: '/pro/fondateur', label: 'Membre Fondateur', icon: Crown },
  { 
    path: '/batiassist', 
    label: 'BatiAssist', 
    icon: Bot,
    isAccordion: true,
    subItems: [
      { path: '/batiassist', label: 'Présentation BatiAssist', icon: Bot },
      { path: '/batiassist/crm', label: 'Mon Cockpit', icon: Building2 },
      { path: '/batiassist/devis', label: 'Devis Intelligents', icon: ClipboardList },
      { path: '/batiassist/pipelines', label: 'Pipelines Commerciaux', icon: BarChart3 },
      { path: '/batiassist/automatisation', label: 'Automatisation & IA', icon: Zap },
      { path: '/batiassist/assistants', label: 'Super Assistants', icon: Users },
      { path: '/batiassist/tarifs', label: 'Tarifs BatiAssist', icon: Tag },
    ]
  },
  { path: '/pro/tarifs', label: 'Tarifs', icon: CreditCard },
  { path: '/reseau-pros', label: 'Réseau Pro', icon: Building2 },
  { path: '/espace-client/dashboard', label: 'Cockpit', icon: Rocket },
];

// Configuration des recherches par section - PLACEHOLDERS DYNAMIQUES
const SEARCH_CONFIG = {
  '/': {
    placeholder: 'Rechercher un artisan, un produit, une annonce...',
    placeholderMobile: 'Rechercher un artisan, un produit...',
    categories: null,
    section: 'accueil',
    sectionLabel: 'Toutes les sections',
    searchPath: '/produits', // Par défaut, redirige vers produits
    filters: ['catégorie', 'ville', 'type'],
  },
  '/artisans': {
    placeholder: 'Rechercher un artisan, un métier, une ville...',
    placeholderMobile: 'Artisan, métier, ville...',
    categories: [
      'Électricien', 'Plombier', 'Maçon', 'Carreleur', 'Peintre', 
      'Menuisier', 'Couvreur', 'Chauffagiste', 'Pisciniste', 'Paysagiste',
      'Charpentier', 'Serrurier', 'Vitrier', 'Climaticien', 'Cuisiniste',
      'Façadier', 'Plâtrier', 'Terrassier', 'Isolation', 'Domotique'
    ],
    categoryLabel: 'Métier',
    section: 'artisans',
    sectionLabel: 'Artisans',
    searchPath: '/artisans',
    filters: ['métier', 'ville', 'note', 'disponibilité'],
  },
  '/produits': {
    placeholder: 'Rechercher un produit, une marque, une référence...',
    placeholderMobile: 'Produit, marque, référence...',
    categories: ['Outillage', 'Plomberie', 'Électricité', 'Peinture', 'Menuiserie'],
    categoryLabel: 'Rayon',
    section: 'marketplace',
    sectionLabel: 'Marketplace',
    searchPath: '/produits',
    filters: ['catégorie', 'prix', 'vendeur certifié', 'livraison'],
  },
  '/marketplace': {
    placeholder: 'Rechercher un produit, une marque, une référence...',
    placeholderMobile: 'Produit, marque, référence...',
    categories: ['Outillage', 'Plomberie', 'Électricité', 'Peinture', 'Menuiserie'],
    categoryLabel: 'Rayon',
    section: 'marketplace',
    sectionLabel: 'Marketplace',
    searchPath: '/produits',
    filters: ['catégorie', 'prix', 'vendeur certifié', 'livraison'],
  },
  '/annonces': {
    placeholder: 'Rechercher une annonce, un matériel, un outil...',
    placeholderMobile: 'Annonce, matériel, outil...',
    categories: ['Matériaux', 'Outillage', 'Véhicules', 'Services'],
    categoryLabel: 'Catégorie',
    section: 'particuliers',
    sectionLabel: 'Petites annonces',
    searchPath: '/particuliers',
    filters: ['catégorie', 'prix', 'ville', 'type d\'annonce'],
  },
  '/particuliers': {
    placeholder: 'Rechercher une annonce, un matériel, un outil...',
    placeholderMobile: 'Annonce, matériel, outil...',
    categories: ['Matériaux', 'Outillage', 'Véhicules', 'Services'],
    categoryLabel: 'Catégorie',
    section: 'particuliers',
    sectionLabel: 'Petites annonces',
    searchPath: '/particuliers',
    filters: ['catégorie', 'prix', 'ville', 'type d\'annonce'],
  },
  '/reseau': {
    placeholder: 'Rechercher une actualité, un pro, un chantier...',
    placeholderMobile: 'Actu, pro, chantier...',
    categories: [
      'Électricien', 'Plombier', 'Maçon', 'Carreleur', 'Peintre', 
      'Menuisier', 'Couvreur', 'Chauffagiste', 'Pisciniste', 'Paysagiste',
      'Charpentier', 'Architecte', 'Décorateur', 'Cuisiniste', 'Domotique'
    ],
    categoryLabel: 'Métier',
    section: 'reseau',
    sectionLabel: 'Actualités',
    searchPath: '/reseau',
    filters: ['type de contenu', 'métier', 'ville'],
    popular: [
      { label: 'Rénovation énergétique', icon: 'TrendingUp' },
      { label: 'Chantiers du moment', icon: 'Star' },
      { label: 'Conseils pros', icon: 'Sparkles' },
      { label: 'Tendances déco', icon: 'TrendingUp' },
    ],
  },
  '/reseau/reels': {
    placeholder: 'Rechercher une vidéo, un créateur, un thème...',
    placeholderMobile: 'Vidéo, créateur, thème...',
    section: 'cinema',
    sectionLabel: 'Cinéma',
    searchPath: '/reseau/reels',
    filters: ['catégorie', 'durée', 'créateur'],
    popular: [
      { label: 'Tutos plomberie', icon: 'TrendingUp' },
      { label: 'Before/After', icon: 'Star' },
      { label: 'Astuces chantier', icon: 'Sparkles' },
      { label: 'Fails & wins', icon: 'TrendingUp' },
    ],
  },
  '/emploi': {
    placeholder: 'Rechercher un métier, un poste, une entreprise...',
    placeholderMobile: 'Métier, poste, entreprise...',
    section: 'emploi',
    sectionLabel: 'Emploi',
    searchPath: '/emploi',
    filters: ['type de contrat', 'métier', 'ville', 'expérience'],
  },
  '/reseau-pros': {
    placeholder: 'Rechercher un professionnel, une entreprise, un partenaire...',
    placeholderMobile: 'Pro, entreprise, partenaire...',
    section: 'reseau-pros',
    sectionLabel: 'Réseau Pro',
    searchPath: '/reseau-pros',
    filters: ['métier', 'ville', 'spécialité', 'disponibilité'],
  },
  '/pro': {
    placeholder: 'Rechercher...',
    placeholderMobile: 'Rechercher...',
    section: 'espace-pro',
    sectionLabel: 'Espace Pro',
    searchPath: '/pro',
  },
  '/batiassist': {
    placeholder: 'Rechercher...',
    placeholderMobile: 'Rechercher...',
    section: 'batiassist',
    sectionLabel: 'BatiAssist',
    searchPath: '/batiassist',
  },
  '/univers': {
    placeholder: 'Rechercher un univers...',
    placeholderMobile: 'Rechercher un univers...',
    section: 'univers',
    sectionLabel: 'Univers',
    searchPath: '/univers',
    filters: ['catégorie'],
  },
};

// ══════════════════════════════════════════════════════════════════════════════
// ONBOARDING AUTO-OUVERTURE LOUPE - Avril à Juin 2026
// ══════════════════════════════════════════════════════════════════════════════

// Vérifier si on est dans la période d'onboarding
// Activé immédiatement pour la démo (petit comité)
const isOnboardingPeriod = () => {
  return true; // Actif dès maintenant
  // Version date originale (pour plus tard si besoin) :
  // const now = new Date();
  // const start = new Date('2026-04-01');
  // const end = new Date('2026-07-01');
  // return now >= start && now < end;
};

// Placeholders animés par page - Messages d'onboarding BTP
const SEARCH_ONBOARDING_MESSAGES = {
  '/': [
    'Recherche un artisan certifié...',
    'Trouve un plombier, électricien, pisciniste...',
    'Achète du matériel sans commission...',
    'Bienvenue — que cherches-tu ?',
  ],
  '/artisans': [
    'Trouve un artisan certifié près de chez toi...',
    'Plombier, électricien, maçon, pisciniste...',
    'Devis gratuit en 24h — artisans vérifiés...',
    'Cherche par métier ou par ville...',
  ],
  '/produits': [
    'Achète sans commission — 0% sur chaque vente...',
    'Matériaux, outillage, équipements BTP...',
    'Vends directement — garde 100% de tes revenus...',
    'Des milliers de produits BTP certifiés...',
  ],
  '/marketplace': [
    'Achète sans commission — 0% sur chaque vente...',
    'Matériaux, outillage, équipements BTP...',
    'Vends directement — garde 100% de tes revenus...',
    'Des milliers de produits BTP certifiés...',
  ],
  '/particuliers': [
    'Petites annonces BTP entre particuliers...',
    'Matériel d\'occasion — contact direct...',
    'Annonces certifiées — zéro arnaque...',
    'Achète, vends, échange sans intermédiaire...',
  ],
  '/annonces': [
    'Petites annonces BTP entre particuliers...',
    'Matériel d\'occasion — contact direct...',
    'Annonces certifiées — zéro arnaque...',
    'Achète, vends, échange sans intermédiaire...',
  ],
  '/reseau': [
    'Cherche un professionnel certifié...',
    'Rejoins la communauté BTP certifiée...',
    'Découvre les pros de ton secteur...',
    '1 personne = 1 compte — zéro faux profil...',
  ],
  '/reseau/reels': [
    'Vidéos BTP tendances...',
    'Tutos, astuces, chantiers...',
    'Découvre les créateurs certifiés...',
    'Inspire-toi des meilleurs pros...',
  ],
  '/emploi': [
    'Trouve un poste dans le BTP...',
    'CDI, CDD, intérim, apprentissage...',
    'Offres d\'emploi BTP certifiées...',
    'Dépose ton CV — contact direct employeur...',
  ],
  '/reseau-pros': [
    'Trouve des partenaires professionnels...',
    'Centrale d\'achat — achète groupé moins cher...',
    'Sous-traitance entre pros certifiés...',
    'Développe ton réseau BTP...',
  ],
  'default': [
    'Recherche sur AbracadaBati...',
    'Artisans, produits, annonces, emploi...',
    'Tout le BTP en un seul endroit...',
    'Certifié — zéro arnaque — zéro commission...',
  ],
};

// Section PRO - Développer mon activité
const PRO_SECTION = [
  { path: '/offre-fondateur', label: 'Ouvrir ma boutique', icon: Store, desc: 'Vente de produits' },
  { path: '/compte', label: 'Recevoir mon Cockpit + agents IA', icon: Bot, desc: 'Automatisation complète' },
  { path: '/artisans', label: 'Gagner en visibilité', icon: Eye, desc: 'Référencement gratuit' },
  { path: '/decrire-projet', label: 'Trouver des chantiers', icon: Target, desc: 'Leads qualifiés' },
  { path: '/reseau-pros', label: 'Accéder au réseau pro', icon: Handshake, desc: 'Networking & entraide' },
  { path: '/reseau-pros', label: 'Centrale d\'achat', icon: Package, desc: 'Achats groupés -30%' },
  { path: '/emploi', label: 'Recruter / publier offres', icon: UserPlus, desc: 'Module emploi' },
];

// Exploration
const EXPLORE_SECTION = [
  { path: '/produits?sort=trending', label: 'Tendances', icon: TrendingUp },
  { path: '/artisans?near=me', label: 'Près de chez moi', icon: MapPin },
  { path: '/produits?sort=popular', label: 'Populaire', icon: Star },
  { path: '/produits?sort=new', label: 'Nouveaux', icon: Clock },
];

export default function UnifiedHeader({ 
  onSearch, 
  onCreateAlert,
  cartCount = 0,
  showCart = false,
  marketplaceMode = false,
  particuliersMode = false,
  initialSearchQuery = '',
  hideSearchOnMobile = false,
  hideSearchOnDesktop = false,
  onSearchClick = null,
  showNetworkSearch = false,
  onOpenFilters = null, // Callback pour ouvrir le panneau de filtres depuis la recherche mobile
  artisansMode = false, // Active la recherche intelligente Artisans
  artisansData = [], // Données artisans pour la recherche intelligente
  globeLinksToWorld = false // Si true, le globe du logo devient cliquable vers /abracadaworld
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isHidden, isAtTop, shouldShowBanner } = useImmersiveScrollContext();
  const { requireAuth } = useDemoMode();
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [searchCity, setSearchCity] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [batiAssistAccordionOpen, setBatiAssistAccordionOpen] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  
  // Désactiver immersive scroll sur les pages Espace Pro (header toujours visible sur desktop)
  const isOnEspaceProPage = location.pathname.startsWith('/pro');
  const isOnEspaceProLanding = location.pathname === '/pro';
  // Note: Sur mobile Espace Pro landing, on activera l'immersif après la déclaration de isMobile
  const effectiveIsHidden = isOnEspaceProPage ? false : isHidden;
  const effectiveShouldShowBanner = isOnEspaceProPage ? true : shouldShowBanner;
  
  // Handler pour le bouton Publier avec vérification auth
  const handlePublishClick = () => {
    if (requireAuth('publier une annonce ou un produit')) {
      setShowPublishModal(true);
    }
  };
  
  // ═══ ÉTAT RECHERCHE FULLSCREEN MOBILE ═══
  const [showMobileFullscreenSearch, setShowMobileFullscreenSearch] = useState(false);
  const mobileSearchInputRef = useRef(null);
  
  // ═══ ÉTAT SMART SEARCH DROPDOWN DESKTOP (Artisans) ═══
  const [showSmartSearchDesktop, setShowSmartSearchDesktop] = useState(false);
  
  // ═══ ONBOARDING OVERLAY PARTIEL (Barre sous navbar) ═══
  const [showOnboardingBar, setShowOnboardingBar] = useState(false);
  const [isClosingOnboarding, setIsClosingOnboarding] = useState(false);
  const [onboardingPlaceholder, setOnboardingPlaceholder] = useState('');
  const onboardingAutoCloseRef = useRef(null);
  const onboardingPlaceholderRef = useRef(null);
  
  // Fermeture avec animation de l'overlay partiel
  const closeOnboardingBar = () => {
    if (onboardingAutoCloseRef.current) clearTimeout(onboardingAutoCloseRef.current);
    if (onboardingPlaceholderRef.current) clearInterval(onboardingPlaceholderRef.current);
    setIsClosingOnboarding(true);
    setTimeout(() => {
      setShowOnboardingBar(false);
      setIsClosingOnboarding(false);
      setOnboardingPlaceholder('');
    }, 300);
  };
  
  // ═══ HISTORIQUE DES RECHERCHES RÉCENTES ═══
  const [recentSearches, setRecentSearches] = useState([]);
  const RECENT_SEARCHES_KEY = 'abracadabati_recent_searches';
  const MAX_RECENT_SEARCHES = 5;
  
  // Charger les recherches récentes au montage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (saved) {
        setRecentSearches(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Erreur chargement recherches récentes:', e);
    }
  }, []);
  
  // Sauvegarder une recherche
  const saveRecentSearch = (query) => {
    if (!query || query.trim().length < 2) return;
    
    const trimmedQuery = query.trim();
    setRecentSearches(prev => {
      // Enlever si déjà présent, ajouter en premier
      const filtered = prev.filter(s => s.toLowerCase() !== trimmedQuery.toLowerCase());
      const updated = [trimmedQuery, ...filtered].slice(0, MAX_RECENT_SEARCHES);
      
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
      } catch (e) {
        console.error('Erreur sauvegarde recherche:', e);
      }
      
      return updated;
    });
  };
  
  // Effacer l'historique
  const clearRecentSearches = () => {
    setRecentSearches([]);
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch (e) {}
  };
  
  // Focus automatique sur l'input quand la recherche fullscreen s'ouvre
  useEffect(() => {
    if (showMobileFullscreenSearch && mobileSearchInputRef.current) {
      setTimeout(() => mobileSearchInputRef.current?.focus(), 250);
    }
  }, [showMobileFullscreenSearch]);
  
  // Marketplace autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  
  // Theme
  const { isDarkMode, toggleTheme } = useTheme();
  
  // Panier - utiliser le contexte si pas de prop
  const buyerContext = useBuyer();
  const actualCartCount = cartCount > 0 ? cartCount : (buyerContext?.cartCount || 0);
  
  // Sync search query when initialSearchQuery changes (for marketplace mode)
  useEffect(() => {
    if ((marketplaceMode || particuliersMode) && initialSearchQuery !== searchQuery) {
      setSearchQuery(initialSearchQuery);
    }
  }, [initialSearchQuery, marketplaceMode, particuliersMode]);
  
  // Détection Espace Pro routes
  const isEspaceProRoute = location.pathname.startsWith('/pro') || 
                           location.pathname.startsWith('/batiassist') ||
                           location.pathname.startsWith('/espace-client') ||
                           location.pathname.startsWith('/reseau-pros');

  // Détection page Cockpit (navbar mobile gérée par le cockpit lui-même)
  const isCockpitRoute = location.pathname.startsWith('/cockpit-pro') || 
                         location.pathname.startsWith('/cockpit-particulier');

  // Détection page À propos (pas de TopBanner, header à top: 0)
  const isAboutPage = location.pathname === '/a-propos';

  // Détection page Marketplace
  const isMarketplacePage = location.pathname === '/produits' || location.pathname.startsWith('/produits/');
  
  // Détection page Particuliers
  const isParticuliersPage = location.pathname === '/particuliers' || location.pathname.startsWith('/particuliers/');

  // Auth state
  const token = localStorage.getItem('buyer_token') || localStorage.getItem('ecosystem_token');
  const isLoggedIn = !!token;
  const [user, setUser] = useState(null);

  // ═══ ONBOARDING NAVBAR STRETCH ═══
  // Non inscrit: 3 passages (T+1s / T+6s / T+11s) — chacun 3s puis se ferme
  // Inscrit: 1 passage (T+1s) — 3s puis se ferme
  
  useEffect(() => {
    // Mobile uniquement (< 768px)
    if (typeof window !== 'undefined' && window.innerWidth >= 768) return;
    
    // Période d'onboarding uniquement
    if (!isOnboardingPeriod()) return;
    
    // Ne pas ouvrir si la recherche fullscreen est déjà ouverte
    if (showMobileFullscreenSearch) return;
    
    // Nettoyer tous les timers précédents
    if (onboardingAutoCloseRef.current) clearTimeout(onboardingAutoCloseRef.current);
    if (onboardingPlaceholderRef.current) clearInterval(onboardingPlaceholderRef.current);
    setShowOnboardingBar(false);
    setIsClosingOnboarding(false);
    
    // Obtenir les messages pour cette route
    const configKey = location.pathname === '/reseau/reels' || location.pathname.startsWith('/reseau/reels/') 
      ? '/reseau/reels' 
      : '/' + (location.pathname.split('/')[1] || '');
    const messages = SEARCH_ONBOARDING_MESSAGES[configKey] 
      || SEARCH_ONBOARDING_MESSAGES['default'];
    
    // Fonction qui lance un passage de 3 secondes
    const runPassage = (startDelay) => {
      return setTimeout(() => {
        let index = 0;
        setOnboardingPlaceholder(messages[0]);
        setIsClosingOnboarding(false);
        setShowOnboardingBar(true);
        
        // Placeholder animé
        onboardingPlaceholderRef.current = setInterval(() => {
          index = (index + 1) % messages.length;
          setOnboardingPlaceholder(messages[index]);
        }, 750);
        
        // Fermer après 3 secondes
        onboardingAutoCloseRef.current = setTimeout(() => {
          if (onboardingPlaceholderRef.current) {
            clearInterval(onboardingPlaceholderRef.current);
          }
          closeOnboardingBar();
        }, 3000);
        
      }, startDelay);
    };
    
    const timers = [];
    
    if (!isLoggedIn) {
      // NON INSCRIT — 3 passages
      timers.push(runPassage(1000));   // T+1s
      timers.push(runPassage(6000));   // T+6s
      timers.push(runPassage(11000));  // T+11s
    } else {
      // INSCRIT — 1 seul passage
      timers.push(runPassage(1000));   // T+1s
    }
    
    return () => {
      timers.forEach(t => clearTimeout(t));
      if (onboardingAutoCloseRef.current) clearTimeout(onboardingAutoCloseRef.current);
      if (onboardingPlaceholderRef.current) clearInterval(onboardingPlaceholderRef.current);
    };
  }, [location.pathname, showMobileFullscreenSearch, isLoggedIn]);

  // Get config for current section - Gestion des sous-routes
  const getConfigKey = () => {
    const path = location.pathname;
    // Vérifier d'abord les routes exactes ou avec sous-routes
    if (path === '/reseau/reels' || path.startsWith('/reseau/reels/')) return '/reseau/reels';
    if (path.startsWith('/reseau-pros')) return '/reseau-pros';
    if (path.startsWith('/pro')) return '/pro';
    if (path.startsWith('/batiassist')) return '/batiassist';
    // Sinon, utiliser le premier segment
    return '/' + (path.split('/')[1] || '');
  };
  
  const currentPath = getConfigKey();
  const isHomePage = location.pathname === '/';
  const config = SEARCH_CONFIG[currentPath] || SEARCH_CONFIG['/'];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (token) {
      fetch(`${API_URL}/api/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => setUser(data))
        .catch(() => {});
    }
  }, [token]);

  // Empêcher le scroll du body quand le menu est ouvert
  useEffect(() => {
    if (showMobileMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showMobileMenu]);

  // Fermer les suggestions autocomplete quand on clique ailleurs
  useEffect(() => {
    if (!marketplaceMode && !particuliersMode) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-testid="header-search-input"]') && 
          !e.target.closest('[data-testid="header-search-mobile"]') &&
          !e.target.closest('[data-testid="search-suggestions-desktop"]') &&
          !e.target.closest('[data-testid="search-suggestions-mobile"]') &&
          !e.target.closest('[data-testid="search-suggestions-particuliers-desktop"]') &&
          !e.target.closest('[data-testid="search-suggestions-particuliers-mobile"]')) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [marketplaceMode, particuliersMode]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (onSearch) {
      onSearch({ query: searchQuery, city: searchCity, category: selectedCategory });
    } else {
      const params = new URLSearchParams();
      if (searchQuery) params.set('q', searchQuery);
      if (searchCity) params.set('city', searchCity);
      if (selectedCategory) params.set('category', selectedCategory);
      
      // Redirect to appropriate page based on current location
      if (isHomePage) {
        navigate(`/produits?${params.toString()}`);
      } else {
        navigate(`${currentPath}?${params.toString()}`);
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('buyer_token');
    localStorage.removeItem('ecosystem_token');
    setUser(null);
    setShowUserMenu(false);
    navigate('/');
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // MARKETPLACE AUTOCOMPLETE - 3 niveaux (Rayon → Sous-rayon → Catégorie)
  // ═══════════════════════════════════════════════════════════════════════════
  
  const RAYONS_COLORS = {
    'rayon-electricite': '#F59E0B', 'rayon-plomberie': '#3B82F6', 'rayon-jardin-paysage': '#22C55E',
    'rayon-piscine-spa': '#06B6D4', 'rayon-peinture-decoration': '#EC4899', 'rayon-maconnerie-gros-oeuvre': '#EA580C',
    'rayon-renovation-interieure': '#6366F1', 'rayon-carrelage-revetement': '#10B981', 'rayon-menuiserie': '#D97706',
    'rayon-chauffage': '#EF4444', 'rayon-couverture-toiture': '#8B5CF6', 'rayon-isolation': '#22C55E',
    'rayon-climatisation-ventilation': '#06B6D4', 'rayon-facade-enduit': '#A855F7', 'rayon-energie-solaire': '#F59E0B',
    'rayon-outillage-equipement': '#64748B',
  };

  const fetchMarketplaceSuggestions = async (query) => {
    if (!marketplaceMode || query.length < 2) {
      setSuggestions([]);
      return;
    }
    setLoadingSuggestions(true);
    try {
      const res = await fetch(`${API_URL}/api/marketplace/search/categories?q=${encodeURIComponent(query)}`);
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.slice(0, 8));
      }
    } catch (e) {
      console.error('Autocomplete error:', e);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Debounce autocomplete
  useEffect(() => {
    if (!marketplaceMode) return;
    const timer = setTimeout(() => {
      fetchMarketplaceSuggestions(searchQuery);
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery, marketplaceMode]);

  const handleSelectSuggestion = (suggestion) => {
    setShowSuggestions(false);
    setSearchQuery(suggestion.match);
    setSuggestions([]);
    if (onSearch) {
      onSearch({
        query: suggestion.match,
        rayon_id: suggestion.rayon_id,
        sous_rayon_id: suggestion.sous_rayon_id || null,
        category_id: suggestion.category_id || null,
      });
    }
  };

  const handleMarketplaceKeyDown = (e) => {
    if (!marketplaceMode || suggestions.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSelectSuggestion(suggestions[selectedSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'rayon': return 'Rayon';
      case 'sous_rayon': return 'Sous-rayon';
      case 'category': return 'Catégorie';
      case 'sous_category': return 'Sous-catégorie';
      case 'type_annonce': return 'Type';
      default: return '';
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // PARTICULIERS AUTOCOMPLETE - Recherche locale instantanée (0ms latency)
  // ═══════════════════════════════════════════════════════════════════════════
  
  const fetchParticuliersSuggestions = (query) => {
    if (!particuliersMode || query.length < 1) {
      setSuggestions([]);
      return;
    }
    // Recherche locale ultra-rapide - pas d'API, 0ms de latence
    const results = getParticuliersSuggestions(query);
    setSuggestions(results.slice(0, 8));
  };

  const handleSelectParticuliersSuggestion = (suggestion) => {
    setShowSuggestions(false);
    setSearchQuery(suggestion.match);
    setSuggestions([]);
    if (onSearch) {
      onSearch({
        query: suggestion.match,
        category_id: suggestion.type === 'category' ? suggestion.id : (suggestion.categoryId || null),
        sous_category_id: suggestion.type === 'sous_category' ? suggestion.id : null,
        type_annonce: suggestion.type === 'type_annonce' ? suggestion.id : null,
      });
    }
  };

  // Debounce autocomplete pour Particuliers (très court car local)
  useEffect(() => {
    if (!particuliersMode) return;
    const timer = setTimeout(() => {
      fetchParticuliersSuggestions(searchQuery);
    }, 50); // 50ms seulement car recherche locale
    return () => clearTimeout(timer);
  }, [searchQuery, particuliersMode]);

  // Mode actif pour autocomplete (marketplace OU particuliers)
  const hasAutocomplete = marketplaceMode || particuliersMode;
  
  const handleAutocompleteKeyDown = (e) => {
    if (!hasAutocomplete || suggestions.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      if (particuliersMode) {
        handleSelectParticuliersSuggestion(suggestions[selectedSuggestionIndex]);
      } else {
        handleSelectSuggestion(suggestions[selectedSuggestionIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Vérifier si on est sur mobile
  const [isMobile, setIsMobile] = useState(false);
  // Vérifier si on est en dessous du breakpoint md (768px) pour le mode selector
  const [isBelowMd, setIsBelowMd] = useState(true);
  // Vérifier si on est sur tablette (pour header fixed)
  const [isTablet, setIsTablet] = useState(false);
  // Vérifier si on est sur Samsung/petit écran (< 420px)
  const [isSamsungSize, setIsSamsungSize] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    const checkBelowMd = () => setIsBelowMd(window.innerWidth < 768);
    const checkTablet = () => setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    const checkSamsung = () => setIsSamsungSize(window.innerWidth < 420);
    checkMobile();
    checkBelowMd();
    checkTablet();
    checkSamsung();
    const handleResize = () => {
      checkMobile();
      checkBelowMd();
      checkTablet();
      checkSamsung();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Header fixed sur mobile ET tablette
  const useFixedHeader = isMobile || isTablet;
  
  // Sur Espace Pro Landing mobile : activer le scroll immersif (header disparaît au scroll)
  const finalEffectiveIsHidden = (isOnEspaceProLanding && isMobile) ? isHidden : effectiveIsHidden;
  
  // Fermer le SmartSearch quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showSmartSearchDesktop && !e.target.closest('[data-testid="smart-search-dropdown"]') && !e.target.closest('[data-testid="header-search-input"]')) {
        setShowSmartSearchDesktop(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSmartSearchDesktop]);
  
  // Calcul du top pour le header fixed
  // Samsung < 420px: 32px (banner plus compacte)
  // Mobile: 36px si banner visible, 0px sinon (immersif)
  // Tablette: toujours 36px (banner toujours fixe)
  const getHeaderTop = () => {
    // Sur Espace Pro, pas de banner = header tout en haut
    if (isEspaceProRoute) return '0px';
    if (isTablet) return '36px'; // Tablette: banner toujours visible
    if (isMobile) {
      if (isSamsungSize) return effectiveShouldShowBanner ? '32px' : '0px'; // Samsung: banner 32px
      return effectiveShouldShowBanner ? '36px' : '0px'; // iPhone: immersif
    }
    return '0px';
  };

  return (
    <>
      <header 
        className="z-50 transition-all duration-200 header-unified"
        style={{
          position: 'fixed',
          top: (isMobile || isTablet) ? getHeaderTop() : ((isEspaceProRoute || isAboutPage) ? '0px' : '40px'),
          left: 0,
          right: 0,
          transition: 'transform 0.22s ease, top 0.22s ease, opacity 0.18s ease',
          transform: (isMobile && finalEffectiveIsHidden) ? 'translateY(-150px)' : 'translateY(0)',
          opacity: (isMobile && finalEffectiveIsHidden) ? 0 : 1,
          pointerEvents: (isMobile && finalEffectiveIsHidden) ? 'none' : 'auto',
          willChange: 'transform, top, opacity',
          zIndex: 100,
          // Fond BLEU NUIT sur Espace Pro (intégré au Hero), blanc/orange doux sur À propos, blanc sinon
          background: isEspaceProRoute 
            ? 'linear-gradient(135deg, #0B1F3A 0%, #1E3A5F 100%)' 
            : (isAboutPage ? '#FFF8F4' : '#FFFFFF'),
          borderBottom: isEspaceProRoute 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : (isAboutPage ? '1px solid rgba(249, 115, 22, 0.12)' : '1px solid rgba(30, 58, 95, 0.08)'),
          boxShadow: isEspaceProRoute 
            ? 'none' 
            : (isScrolled ? '0 4px 12px rgba(15, 23, 42, 0.08)' : '0 4px 12px rgba(15, 23, 42, 0.04)'),
        }}
      >
        {/* ═══════════════════════════════════════════════════════════════
            MOBILE HEADER (< 640px)
            MASQUÉ: TopBanner gère maintenant la navbar mobile unifiée sur les pages publiques
            Affiche seulement sur Espace Pro qui a sa propre gestion
        ═══════════════════════════════════════════════════════════════ */}
        {false && (
        <div className="sm:hidden header-mobile">
          
          {/* LIGNE 1 : Header principal — 52px height (46px sur Samsung < 420px) */}
          <div 
            className="header-top-row flex items-center justify-between samsung-header-height"
            style={{ 
              height: '52px',
              background: isEspaceProRoute ? 'transparent' : '#ffffff',
              padding: '0 12px',
              borderBottom: isEspaceProRoute ? '1px solid rgba(255,255,255,0.1)' : '1px solid #f0f0f0',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
          
          {/* CSS pour Samsung/petits écrans < 420px */}
          <style>{`
            @media (max-width: 419px) {
              .samsung-header-height {
                height: 44px !important;
                padding: 0 8px !important;
              }
              .samsung-header-height img[alt="AbracadaBati"] {
                height: 28px !important;
              }
              .samsung-header-height button[data-testid="header-signup-btn-mobile"] {
                font-size: 11px !important;
                padding: 5px 10px !important;
              }
              .samsung-header-height .w-7 {
                width: 24px !important;
                height: 24px !important;
              }
            }
          `}</style>
            
            {/* ═══ GAUCHE : Logo ═══ */}
            <div className="flex items-center" style={{ gap: '0px' }}>
              {/* Logo avec globe cliquable si globeLinksToWorld */}
              <div className="relative">
                {/* Zone cliquable sur le globe (partie gauche du logo) */}
                {globeLinksToWorld && (
                  <Link
                    to="/abracadaworld"
                    className="absolute z-10"
                    style={{
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                    }}
                    data-testid="header-globe-link-mobile"
                    title="AbracadaWorld"
                  />
                )}
                <Link 
                  to="/" 
                  className="flex items-center" 
                  data-testid="header-logo-mobile"
                >
                  {isEspaceProRoute ? (
                    <img 
                      src="https://customer-assets.emergentagent.com/job_organize-smart/artifacts/mfjbxicz_abracadaworld-02-04%202.svg" 
                      alt="AbracadaPro"
                      style={{ 
                        height: '38px', 
                        width: 'auto',
                      }}
                    />
                  ) : (
                    <img 
                      src="https://customer-assets.emergentagent.com/job_organize-smart/artifacts/4450nlnf_abracadabait_logo_transparent.png" 
                      alt="AbracadaBati"
                      style={{ 
                        height: '32px', 
                        width: 'auto',
                      }}
                    />
                  )}
                </Link>
              </div>
              
              {/* Lien vers AbracadaWorld - Mobile (caché si globe cliquable ou sur Espace Pro) */}
              {!globeLinksToWorld && !isEspaceProRoute && (
                <Link
                  to="/abracadaworld"
                  className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#E8650A] shadow-md ml-1"
                  title="AbracadaWorld"
                  data-testid="header-abracadaworld-link-mobile"
                >
                  <span className="text-white font-bold text-xs">A</span>
                </Link>
              )}
            </div>
            
            {/* ═══ DROITE : Actions ═══ */}
            <div className="flex items-center" style={{ gap: '8px' }}>
              {!isLoggedIn && (
                <button
                  onClick={() => navigate('/reseau/inscription')}
                  style={{ 
                    border: '1.5px solid #E8650A',
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #E8650A 0%, #ff7b1c 100%)',
                    color: '#ffffff',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: '11px',
                    fontWeight: '600',
                    padding: '5px 12px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    WebkitTapHighlightColor: 'transparent',
                    boxShadow: '0 2px 8px rgba(232, 101, 10, 0.3)',
                  }}
                  data-testid="header-signup-btn-mobile"
                >
                  S'inscrire
                </button>
              )}

              {/* ── CONNECTÉ : Avatar + Notifications ── */}
              {isLoggedIn && (
                <>
                  {/* Avatar - Lien direct vers Cockpit */}
                  <Link
                    to={user?.role === 'pro' ? '/cockpit-pro' : '/cockpit-particulier'}
                    style={{ 
                      padding: '4px',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                    data-testid="header-user-avatar-mobile"
                  >
                    {user?.profile_photo ? (
                      <img 
                        src={user.profile_photo} 
                        alt="Mon profil"
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '2px solid #F97316',
                        }}
                      />
                    ) : (
                      <div 
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #F97316, #EA580C)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid rgba(249, 115, 22, 0.3)',
                        }}
                      >
                        <span style={{ color: '#ffffff', fontWeight: '700', fontSize: '13px' }}>
                          {user?.display_name?.charAt(0) || user?.name?.charAt(0) || 'U'}
                        </span>
                      </div>
                    )}
                  </Link>
                  
                  {/* Notifications */}
                  <button 
                    onClick={() => navigate('/notifications')}
                    className="relative"
                    style={{ 
                      padding: '8px',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <Bell size={20} style={{ color: '#1a2a4a' }} />
                    <span 
                      style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        width: '16px',
                        height: '16px',
                        background: '#EF4444',
                        borderRadius: '50%',
                        border: '2px solid #ffffff',
                        fontSize: '9px',
                        fontWeight: '700',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      3
                    </span>
                  </button>
                </>
              )}

              {/* Panier - Toujours visible pour tous */}
              <button 
                onClick={() => navigate('/panier')}
                className="relative"
                style={{ 
                  padding: '8px',
                  WebkitTapHighlightColor: 'transparent',
                }}
                data-testid="header-cart-mobile-global"
              >
                <ShoppingCart size={20} style={{ color: '#1a2a4a' }} />
                {actualCartCount > 0 && (
                  <span 
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      minWidth: '16px',
                      height: '16px',
                      padding: '0 4px',
                      background: '#F97316',
                      borderRadius: '50%',
                      border: '2px solid #ffffff',
                      fontSize: '9px',
                      fontWeight: '700',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {actualCartCount > 99 ? '99+' : actualCartCount}
                  </span>
                )}
              </button>

              {/* Loupe recherche orange - MASQUÉE sur Espace Pro (menu hamburger à la place) */}
              {!isEspaceProRoute && (
                <button
                  onClick={() => setShowMobileFullscreenSearch(true)}
                  className="flex items-center justify-center"
                  style={{ 
                    width: '32px',
                    height: '32px',
                    background: 'linear-gradient(135deg, #E8650A 0%, #ff7b1c 100%)',
                    borderRadius: '10px',
                    border: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    boxShadow: '0 2px 8px rgba(232, 101, 10, 0.35)',
                  }}
                  data-testid="header-search-icon-mobile"
                >
                  <Search size={15} style={{ color: '#ffffff' }} />
                </button>
              )}
            </div>
            
            {/* ═══ ONBOARDING STRETCH ANIMATION - MASQUÉE sur Espace Pro ═══ */}
            {/* Droite vers Gauche - Optimisé Galaxy A54/A55 (390px) */}
            {showOnboardingBar && !isEspaceProRoute && (
              <div
                className="sm:hidden"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  zIndex: 100,
                  background: '#FFF8F4',
                  overflow: 'hidden',
                  padding: '0 12px',
                  boxSizing: 'border-box',
                  animation: isClosingOnboarding
                    ? 'shrinkToRight 0.4s ease-out forwards'
                    : 'stretchFromRight 0.4s ease-out forwards',
                }}
                data-testid="onboarding-stretch-bar"
              >
                {/* Loupe orange à GAUCHE */}
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    minWidth: '40px',
                    background: '#E8650A',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Search size={18} className="text-white" />
                </div>

                {/* Input - prend tout l'espace restant */}
                <input
                  readOnly
                  value=""
                  placeholder={onboardingPlaceholder}
                  onClick={() => {
                    if (onboardingPlaceholderRef.current) {
                      clearInterval(onboardingPlaceholderRef.current);
                    }
                    closeOnboardingBar();
                    setTimeout(() => setShowMobileFullscreenSearch(true), 100);
                  }}
                  style={{
                    flex: 1,
                    height: '40px',
                    marginLeft: '8px',
                    border: '2px solid #E8650A',
                    borderRadius: '20px',
                    padding: '0 16px',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    background: '#FFFFFF',
                    outline: 'none',
                    cursor: 'pointer',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  data-testid="onboarding-stretch-input"
                />
              </div>
            )}
          </div>
          
          {/* LIGNE 2 : Barre de recherche - MASQUÉE sur mobile (loupe en haut suffit) */}
          {/* Sur desktop, cette barre reste visible */}
          {!hideSearchOnMobile && !showNetworkSearch && (
          <div className="header-search-row px-3 pb-2.5 hidden">
            <div 
              onClick={() => setShowMobileFullscreenSearch(true)}
              className="header-search-box flex items-center w-full bg-[#F8FAFC] cursor-pointer"
              style={{
                height: '48px',
                border: '2px solid #E2E8F0',
                borderRadius: '28px',
                padding: '0 16px 0 6px',
              }}
              data-testid="header-search-trigger-mobile"
            >
              {/* Bouton loupe orange 40px à GAUCHE uniquement */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMobileFullscreenSearch(true);
                }}
                className="flex items-center justify-center flex-shrink-0"
                style={{ 
                  width: '40px', 
                  height: '40px', 
                  minWidth: '40px',
                  background: '#E8650A', 
                  borderRadius: '50%',
                  WebkitTapHighlightColor: 'transparent',
                }}
                data-testid="header-search-btn-orange"
              >
                <Search size={18} className="text-white" />
              </button>
              <div 
                className="flex-1 text-[#94A3B8] truncate ml-3"
                style={{ fontSize: '14px' }}
              >
                {config.placeholderMobile || config.placeholder}
              </div>
            </div>
          </div>
          )}
          
          {/* Loupe seule pour page Actu (showNetworkSearch) - MASQUÉE aussi */}
          {showNetworkSearch && onSearchClick && (
            <div className="header-search-row px-3 pb-2.5 hidden">
              <div 
                onClick={onSearchClick}
                className="header-search-box flex items-center w-full bg-[#F8FAFC] cursor-pointer"
                style={{
                  height: '48px',
                  border: '2px solid #E2E8F0',
                  borderRadius: '28px',
                  padding: '0 16px 0 6px',
                }}
              >
                {/* Loupe orange à GAUCHE uniquement */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSearchClick();
                  }}
                  className="flex items-center justify-center flex-shrink-0"
                  style={{ 
                    width: '40px', height: '40px', minWidth: '40px',
                    background: '#E8650A', borderRadius: '50%',
                  }}
                >
                  <Search size={18} className="text-white" />
                </button>
                <div className="flex-1 text-[#94A3B8] truncate ml-3" style={{ fontSize: '14px' }}>
                  {config.placeholderMobile || config.placeholder}
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════
            HEADER DESKTOP - Structure classique
        ═══════════════════════════════════════════════════════════════ */}
        <div 
          className={`hidden sm:block ${isEspaceProRoute ? '' : 'border-b border-gray-100'}`}
          style={isEspaceProRoute ? { paddingTop: '8px' } : {}} // Espacement bandeau → header
        >
          <div className={`max-w-7xl mx-auto ${isEspaceProRoute ? 'px-6' : 'px-4'}`}>
            <div className="flex items-center h-16 gap-3 lg:gap-4">
              
              {/* Logo avec globe TOUJOURS cliquable sur Desktop */}
              <div className="relative flex-shrink-0" style={{ marginLeft: isEspaceProRoute ? '0px' : '40px', marginRight: isEspaceProRoute ? '20px' : '0px' }}>
                {/* Zone cliquable sur le globe (partie gauche du logo) - TOUJOURS actif sur desktop */}
                <Link
                  to="/abracadaworld"
                  className="absolute z-10 hover:scale-110 transition-transform"
                  style={{
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                  }}
                  data-testid="header-globe-link-desktop"
                  title="Accéder à AbracadaWorld"
                />
                <Link 
                  to="/" 
                  className="flex items-center gap-2"
                  data-testid="header-logo"
                >
                  {isEspaceProRoute ? (
                    <img 
                      src="https://customer-assets.emergentagent.com/job_organize-smart/artifacts/mfjbxicz_abracadaworld-02-04%202.svg" 
                      alt="AbracadaPro"
                      style={{ height: '70px', width: 'auto' }}
                    />
                  ) : (
                    <img 
                      src="https://customer-assets.emergentagent.com/job_organize-smart/artifacts/4450nlnf_abracadabait_logo_transparent.png" 
                      alt="AbracadaBati"
                      className="h-12 w-auto"
                    />
                  )}
                </Link>
              </div>
              
              {/* Cercle "A" supprimé sur desktop - le globe suffit */}

              {/* Search Bar - Style Premium avec Autocomplete Marketplace/Particuliers */}
              {/* Masquée sur desktop si hideSearchOnDesktop */}
              {!hideSearchOnDesktop && (
              <form 
                onSubmit={handleSearch}
                className="flex-1 flex items-center max-w-2xl relative"
              >
                <div 
                  className={`flex items-center w-full rounded-xl overflow-hidden shadow-sm transition-all border border-gray-200 ${
                    artisansMode 
                      ? 'focus-within:border-[#F97316]/40 focus-within:shadow-md focus-within:shadow-[#F97316]/10'
                      : particuliersMode 
                        ? 'focus-within:border-[#F97316]/40 focus-within:shadow-md focus-within:shadow-[#F97316]/10' 
                        : 'focus-within:border-[#F97316]/40 focus-within:shadow-md focus-within:shadow-[#F97316]/10'
                  }`}
                  style={{ background: '#FFFFFF' }}
                >
                  
                  {/* Search button - LOUPE ORANGE */}
                  <Button
                    type="submit"
                    variant="ghost"
                    className="h-11 px-4 text-white rounded-none rounded-l-xl text-sm font-medium transition-colors !bg-[#F97316] hover:!bg-[#EA580C] flex-shrink-0"
                    data-testid="header-search-btn"
                  >
                    <Search size={18} />
                  </Button>
                  
                  {/* Category dropdown - masqué en mode marketplace/particuliers/artisans */}
                  {config.categories && !marketplaceMode && !particuliersMode && !artisansMode && (
                    <div className="hidden lg:block relative border-r border-gray-200">
                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="h-11 pl-4 pr-8 bg-transparent text-gray-700 text-sm font-medium focus:outline-none cursor-pointer appearance-none"
                      >
                        <option value="">{config.categoryLabel || 'Tous'}</option>
                        {config.categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                      <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  )}

                  {/* Main search input - SANS loupe grise */}
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        if (artisansMode) {
                          setShowSmartSearchDesktop(true);
                        } else if (hasAutocomplete) {
                          setShowSuggestions(true);
                          setSelectedSuggestionIndex(-1);
                        }
                      }}
                      onFocus={() => {
                        if (artisansMode) {
                          setShowSmartSearchDesktop(true);
                        } else if (hasAutocomplete && suggestions.length > 0) {
                          setShowSuggestions(true);
                        }
                      }}
                      onKeyDown={handleAutocompleteKeyDown}
                      placeholder={
                        artisansMode
                          ? 'Rechercher un artisan, un métier, une ville...'
                          : particuliersMode 
                            ? 'Rechercher une annonce, outillage, matériaux...' 
                            : marketplaceMode 
                              ? 'Rechercher un produit, une marque, un rayon...' 
                              : config.placeholder
                      }
                      className="w-full h-11 pl-4 pr-3 bg-transparent text-gray-900 text-sm placeholder-gray-400 focus:outline-none"
                      data-testid="header-search-input"
                    />
                    {marketplaceMode && loadingSuggestions && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-[#F97316] border-t-transparent rounded-full animate-spin" />
                    )}
                    {(hasAutocomplete || artisansMode) && searchQuery && !loadingSuggestions && (
                      <button 
                        type="button" 
                        onClick={() => { setSearchQuery(''); setSuggestions([]); setShowSmartSearchDesktop(false); }} 
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                      >
                        <X size={14} className="text-gray-400" />
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Smart Search Dropdown - Desktop (Artisans) */}
                {artisansMode && showSmartSearchDesktop && (
                  <SmartSearchDropdown
                    isOpen={showSmartSearchDesktop}
                    onClose={() => setShowSmartSearchDesktop(false)}
                    onSearch={(params) => {
                      if (onSearch) onSearch(params);
                      setShowSmartSearchDesktop(false);
                    }}
                    onOpenFilters={onOpenFilters}
                    artisans={artisansData}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                  />
                )}
                
                {/* Autocomplete Dropdown - Desktop (Marketplace) */}
                {marketplaceMode && showSuggestions && suggestions.length > 0 && (
                  <div 
                    className="absolute left-0 right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50"
                    data-testid="search-suggestions-desktop"
                  >
                    {suggestions.map((s, idx) => {
                      const color = RAYONS_COLORS[s.rayon_id] || '#6B7280';
                      return (
                        <button
                          key={`${s.type}-${s.rayon_id}-${idx}`}
                          type="button"
                          onClick={() => handleSelectSuggestion(s)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                            selectedSuggestionIndex === idx ? 'bg-gray-50' : ''
                          }`}
                        >
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
                            <Package size={18} style={{ color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{s.match}</p>
                            <p className="text-xs text-gray-500 truncate">
                              {s.type === 'rayon' && s.rayon_name}
                              {s.type === 'sous_rayon' && `${s.rayon_name} › ${s.sous_rayon_name}`}
                              {s.type === 'category' && `${s.rayon_name} › ${s.sous_rayon_name}`}
                            </p>
                          </div>
                          <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: `${color}15`, color }}>
                            {getTypeLabel(s.type)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                
                {/* Autocomplete Dropdown - Desktop (Particuliers) */}
                {particuliersMode && showSuggestions && suggestions.length > 0 && (
                  <div 
                    className="absolute left-0 right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50"
                    data-testid="search-suggestions-particuliers-desktop"
                  >
                    {suggestions.map((s, idx) => {
                      const color = s.color || '#8B5CF6';
                      const IconComponent = PARTICULIERS_ICONS_MAP[s.icon] || Package;
                      return (
                        <button
                          key={`${s.type}-${s.id}-${idx}`}
                          type="button"
                          onClick={() => handleSelectParticuliersSuggestion(s)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                            selectedSuggestionIndex === idx ? 'bg-gray-50' : ''
                          }`}
                        >
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
                            <IconComponent size={18} style={{ color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{s.name}</p>
                            <p className="text-xs text-gray-500 truncate">
                              {s.type === 'category' && s.name}
                              {s.type === 'sous_category' && `${s.categoryName} › ${s.name}`}
                              {s.type === 'type_annonce' && 'Type d\'annonce'}
                            </p>
                          </div>
                          <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: `${color}15`, color }}>
                            {getTypeLabel(s.type)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </form>
              )}
              
              {/* Spacer pour garder les boutons ancrés à droite quand la recherche est masquée */}
              {hideSearchOnDesktop && <div className="flex-1" />}

              {/* CTA Fondateur - Desktop + Tablette - VERT emerald (EN PREMIER) */}
              <Link 
                to="/pro/fondateur"
                className="hidden md:flex items-center"
              >
                <Button 
                  className="h-10 px-4 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl font-semibold transition-all"
                  style={{ boxShadow: '0 6px 16px rgba(34, 197, 94, 0.25)' }}
                  data-testid="header-cta-fondateur"
                >
                  <Rocket size={16} className="mr-2" />
                  <span>Devenir Fondateur</span>
                  <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-[10px] font-bold">
                    1 AN OFFERT
                  </span>
                </Button>
              </Link>

              {/* Bouton Connexion - Desktop (>1024px) - Visible uniquement si non connecté (AU MILIEU) */}
              {!isLoggedIn && (
                <Link to="/connexion">
                  <Button
                    variant="outline"
                    className="hidden lg:flex items-center h-10 px-4 border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white rounded-xl font-semibold transition-all"
                    data-testid="header-connexion-btn"
                  >
                    <User size={18} className="mr-1.5" />
                    <span>Connexion</span>
                  </Button>
                </Link>
              )}

              {/* Cart (for marketplace) */}
              {showCart && (
                <Link 
                  to="/panier"
                  className="relative p-2 hover:bg-gray-100 rounded-xl transition-colors"
                  data-testid="header-cart"
                >
                  <ShoppingCart size={20} className="text-gray-700" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#F97316] text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Link>
              )}

              {/* Mode Selector (Shop/Réseau) - Mobile, Credit Badge - Desktop */}
              {isBelowMd ? (
                <ModeSelector />
              ) : (
                isLoggedIn && <CreditBadge className="mr-1" />
              )}

              {/* Notifications - visible only if logged in */}
              {isLoggedIn && <NotificationCenter />}

              {/* User Avatar - Direct link to Cockpit (no dropdown) */}
              <div className="relative">
                {isLoggedIn ? (
                  <Link
                    to={user?.role === 'pro' ? '/cockpit-pro' : '/cockpit-particulier'}
                    className="flex items-center gap-2 p-1.5 hover:bg-gray-100 rounded-xl transition-colors"
                    data-testid="header-user-btn"
                  >
                    {user?.profile_photo ? (
                      <img 
                        src={user.profile_photo} 
                        alt="Mon profil"
                        className="w-9 h-9 rounded-full object-cover border-2 border-[#F97316]"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center border-2 border-[#F97316]/30">
                        <span className="text-white font-bold text-sm">
                          {user?.display_name?.charAt(0) || user?.name?.charAt(0) || 'U'}
                        </span>
                      </div>
                    )}
                  </Link>
                ) : (
                  <Link to="/reseau/inscription">
                    <Button 
                      className="h-10 px-4 bg-[#F97316] hover:bg-[#EA580C] text-white rounded-xl font-semibold text-sm"
                      data-testid="header-signup-btn"
                    >
                      <User size={16} className="mr-1.5" />
                      <span className="hidden md:inline">Créer un compte</span>
                      <span className="md:hidden">S'inscrire</span>
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Spacer mobile pour compenser le header fixed (52px) */}
      {isMobile && (
        <div 
          style={{ 
            height: '52px',
            background: isEspaceProRoute ? '#072B57' : 'transparent',
          }} 
          className="sm:hidden" 
        />
      )}
      
      {/* Spacer tablette pour compenser le header fixed (64px = h-16) */}
      {isTablet && (
        <div 
          style={{ 
            height: '64px',
            background: isEspaceProRoute ? 'linear-gradient(165deg, #072B57 0%, #103A6D 35%)' : 'transparent',
          }} 
          className="hidden sm:block lg:hidden" 
        />
      )}
      
      {/* Spacer desktop pour compenser le header fixed (64px = h-16) */}
      <div 
        style={{ 
          height: '64px',
          background: isEspaceProRoute ? 'linear-gradient(165deg, #072B57 0%, #103A6D 35%)' : 'transparent',
        }} 
        className="hidden lg:block" 
      />

      {/* ═══════════════════════════════════════════════════════════════
          RECHERCHE FULLSCREEN MOBILE - Style Stadium/Pill
          Orange = demi-pill qui remplit le côté gauche
      ═══════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showMobileFullscreenSearch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="sm:hidden"
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: '#ffffff',
              zIndex: 99999,
            }}
          >
            {/* X fermer en haut à droite */}
            <div 
              className="flex items-center justify-end bg-white"
              style={{ 
                padding: '8px 12px',
                paddingTop: 'max(8px, env(safe-area-inset-top, 8px))',
              }}
            >
              <button
                onClick={() => setShowMobileFullscreenSearch(false)}
                style={{ width: '32px', height: '32px' }}
                data-testid="fullscreen-search-close"
              >
                <X size={24} className="text-[#64748B]" strokeWidth={2} />
              </button>
            </div>
            
            {/* BARRE DE RECHERCHE - Style Stadium EXACT comme l'image */}
            <div className="px-3 pb-3">
              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  if (searchQuery.trim()) {
                    saveRecentSearch(searchQuery);
                    setShowMobileFullscreenSearch(false);
                    if (onSearch) {
                      onSearch({ query: searchQuery, city: searchCity, category: selectedCategory });
                    } else {
                      const params = new URLSearchParams();
                      if (searchQuery) params.set('q', searchQuery);
                      // Utilise le searchPath de la config pour rester dans la bonne section
                      const targetPath = config.searchPath || '/produits';
                      navigate(`${targetPath}?${params.toString()}`);
                    }
                  }
                }}
                className="flex items-center w-full"
                style={{
                  height: '56px',
                  border: '2px solid #C9CDD4',
                  borderRadius: '28px',
                  background: '#F8F9FA',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {/* ORANGE DEMI-STADIUM - Épouse parfaitement le bord gauche */}
                <motion.button
                  type="submit"
                  initial={{ x: 320, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ 
                    type: 'spring', 
                    stiffness: 280, 
                    damping: 24,
                    delay: 0.03 
                  }}
                  className="flex items-center justify-center flex-shrink-0"
                  style={{ 
                    width: '56px', 
                    height: '56px',
                    background: '#E8650A',
                    borderRadius: '26px 0 0 26px',
                    position: 'relative',
                    left: '-2px',
                    top: '0',
                  }}
                  data-testid="fullscreen-search-submit"
                >
                  <Search size={24} className="text-white" strokeWidth={2} />
                </motion.button>
                
                {/* Input de recherche fullscreen */}
                <input
                  ref={mobileSearchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={config.placeholderMobile || config.placeholder}
                  className="flex-1 bg-transparent text-[#333] placeholder-[#999] focus:outline-none"
                  style={{ 
                    fontSize: '16px',
                    paddingLeft: '12px',
                    paddingRight: '16px',
                  }}
                  autoFocus
                  data-testid="fullscreen-search-input"
                />
                
                {/* Clear */}
                {searchQuery && (
                  <button 
                    type="button" 
                    onClick={() => setSearchQuery('')}
                    className="flex items-center justify-center mr-4"
                    style={{
                      width: '22px',
                      height: '22px',
                      background: '#C9CDD4',
                      borderRadius: '50%',
                    }}
                  >
                    <X size={14} className="text-white" strokeWidth={2.5} />
                  </button>
                )}
              </form>
            </div>
            
            {/* CTA PRINCIPAL - Ouvrir le filtre intelligent */}
            {onOpenFilters && (
              <motion.div 
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="px-4 py-4"
              >
                <button
                  onClick={() => {
                    setShowMobileFullscreenSearch(false);
                    setTimeout(() => onOpenFilters(), 150);
                  }}
                  className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-semibold text-white transition-all active:scale-[0.98]"
                  style={{
                    background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)',
                    boxShadow: '0 4px 14px rgba(30, 58, 95, 0.25)',
                  }}
                  data-testid="mobile-search-open-filters"
                >
                  <SlidersHorizontal size={20} strokeWidth={2} />
                  <span style={{ fontSize: '15px' }}>
                    {config.section === 'artisans' && 'Trouve ton artisan en 3 clics'}
                    {config.section === 'marketplace' && 'Trouve ton produit en 3 clics'}
                    {config.section === 'particuliers' && 'Trouve ton annonce en 3 clics'}
                    {config.section === 'emploi' && 'Trouve ton emploi en 3 clics'}
                    {config.section === 'reseau' && 'Trouve ton contenu en 3 clics'}
                    {!['artisans', 'marketplace', 'particuliers', 'emploi', 'reseau'].includes(config.section) && 'Filtrer les résultats'}
                  </span>
                </button>
              </motion.div>
            )}
            
            {/* Anciens mini-filtres - Masqués si onOpenFilters est fourni */}
            {!onOpenFilters && config.filters && (
              <motion.div 
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="px-4 py-2.5 border-b border-gray-100"
                style={{ background: '#FAFAFA' }}
              >
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
                  <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase whitespace-nowrap tracking-wide">
                    Filtres
                  </span>
                  {config.filters.map((filter, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1.5 bg-white rounded-full text-[12px] text-[#374151] whitespace-nowrap font-medium"
                      style={{ border: '1px solid #E5E7EB' }}
                    >
                      {filter}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}
            
            {/* Contenu - Populaires + Recherches récentes */}
            <div className="p-4 bg-white flex-1 overflow-y-auto">
              <p className="text-[11px] text-[#E8650A] uppercase font-semibold tracking-wide mb-4">
                Recherche dans : {config.sectionLabel || config.section || 'toutes les sections'}
              </p>
              
              {/* Section Populaire (Cinéma & Actualités uniquement) */}
              {config.popular && config.popular.length > 0 && (
                <div className="mb-5">
                  <p className="text-[10px] text-[#9CA3AF] uppercase font-semibold tracking-wide mb-2">
                    Populaire
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {config.popular.map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setSearchQuery(item.label);
                          saveRecentSearch(item.label);
                          setShowMobileFullscreenSearch(false);
                          const params = new URLSearchParams();
                          params.set('q', item.label);
                          const targetPath = config.searchPath || '/produits';
                          navigate(`${targetPath}?${params.toString()}`);
                        }}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-full transition-colors"
                        style={{ 
                          background: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)',
                          border: '1px solid #FDBA74',
                        }}
                        data-testid={`popular-search-${idx}`}
                      >
                        <TrendingUp size={14} className="text-[#E8650A]" />
                        <span className="text-[13px] text-[#9A3412] font-medium">{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Recherches récentes */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-[#9CA3AF] uppercase font-semibold tracking-wide">
                    Recherches récentes
                  </p>
                  {recentSearches.length > 0 && (
                    <button
                      onClick={clearRecentSearches}
                      className="text-[11px] text-[#E8650A] font-medium"
                    >
                      Effacer
                    </button>
                  )}
                </div>
                
                {recentSearches.length > 0 ? (
                  <div className="space-y-1">
                    {recentSearches.map((search, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setSearchQuery(search);
                          saveRecentSearch(search);
                          setShowMobileFullscreenSearch(false);
                          if (onSearch) {
                            onSearch({ query: search, city: searchCity, category: selectedCategory });
                          } else {
                            const params = new URLSearchParams();
                            params.set('q', search);
                            // Utilise le searchPath de la config pour rester dans la bonne section
                            const targetPath = config.searchPath || '/produits';
                            navigate(`${targetPath}?${params.toString()}`);
                          }
                        }}
                        className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
                        data-testid={`recent-search-${idx}`}
                      >
                        <Clock size={16} className="text-[#9CA3AF] flex-shrink-0" />
                        <span className="text-[14px] text-[#374151] truncate">{search}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-[14px] text-[#9CA3AF] text-center py-6">
                    <Search size={24} className="mx-auto mb-2 text-[#E5E7EB]" />
                    <p>Aucune recherche récente</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════
          MENU HAMBURGER PREMIUM - Slide-in depuis la GAUCHE
      ═══════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showMobileMenu && (
          <>
            {/* Overlay */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50 lg:hidden"
              onClick={() => setShowMobileMenu(false)}
            />
            
            {/* Panel - GAUCHE */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed top-0 left-0 bottom-0 w-[85%] max-w-sm bg-white z-50 lg:hidden overflow-y-auto shadow-2xl"
            >
              {/* Header du menu avec padding pour safe area */}
              <div 
                className="sticky top-0 bg-white border-b border-gray-100 z-10"
                style={{ paddingTop: 'max(44px, env(safe-area-inset-top, 44px))' }}
              >
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    {isLoggedIn ? (
                      <>
                        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#1E40AF] to-[#3B82F6] flex items-center justify-center">
                          <User size={20} className="text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {user?.display_name || user?.name || 'Mon compte'}
                          </p>
                          <p className="text-xs text-gray-500">{user?.email || ''}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center">
                          <User size={20} className="text-gray-400" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">Mon compte</p>
                          <p className="text-xs text-gray-500">Non connecté</p>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => setShowMobileMenu(false)}
                    className="p-2 hover:bg-gray-100 rounded-xl min-h-[44px] min-w-[44px] flex items-center justify-center"
                  >
                    <X size={22} className="text-gray-500" />
                  </button>
                </div>

                {/* CTA Devenir Fondateur - Affiché UNIQUEMENT hors Espace Pro */}
                {!isEspaceProRoute && (
                  <div className="px-4 pb-4">
                    <Link 
                      to="/offre-fondateur"
                      onClick={() => setShowMobileMenu(false)}
                      className="block"
                    >
                      <div className="relative bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl p-3 overflow-hidden">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                        <div className="relative flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                            <Rocket size={20} className="text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-bold text-sm">Devenir Fondateur</p>
                            <p className="text-emerald-100 text-xs">Programme artisans • 1 an offert</p>
                          </div>
                          <ChevronRight size={20} className="text-white/70 flex-shrink-0" />
                        </div>
                      </div>
                    </Link>
                  </div>
                )}
              </div>

              {/* Contenu du menu - Conditionnel : Espace Pro ou Standard */}
              <div className="p-4 space-y-6">
                
                {isEspaceProRoute ? (
                  /* ═══════════════════════════════════════════════════════════════
                      MENU ESPACE PRO - Conditionnel pour routes /pro/*
                  ═══════════════════════════════════════════════════════════════ */
                  <>
                    {/* ══════ LIEN RETOUR CENTRE COMMERCIAL ══════ */}
                    <div>
                      <Link
                        to="/"
                        onClick={() => setShowMobileMenu(false)}
                        className="flex items-center gap-3 px-4 py-4 bg-gradient-to-r from-[#1E3A5F] to-[#2D4A6F] rounded-2xl text-white hover:shadow-lg transition-all"
                        data-testid="mobile-menu-centre-commercial"
                      >
                        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                          <Store size={20} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-white">Centre Commercial</p>
                          <p className="text-sm text-white/70">Retour à l'accueil</p>
                        </div>
                        <ChevronRight size={20} className="text-white/50" />
                      </Link>
                    </div>

                    {/* ══════ SECTION ESPACE PRO ══════ */}
                    <div className="bg-gradient-to-br from-[#F97316]/5 to-[#F97316]/10 rounded-2xl p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-lg bg-[#F97316] flex items-center justify-center">
                          <Crown size={16} className="text-white" />
                        </div>
                        <p className="font-bold text-gray-900">Espace Pro</p>
                      </div>
                      <nav className="space-y-1">
                        {ESPACE_PRO_MOBILE_MENU.map((item, index) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.path;
                          
                          // Item avec accordéon (BatiAssist)
                          if (item.isAccordion) {
                            return (
                              <div key={index}>
                                {/* Bouton accordéon - Touch target 44px */}
                                <button
                                  onClick={() => setBatiAssistAccordionOpen(!batiAssistAccordionOpen)}
                                  className={`flex items-center justify-between w-full px-4 min-h-[44px] rounded-xl transition-colors ${
                                    batiAssistAccordionOpen || location.pathname.startsWith('/batiassist')
                                      ? 'bg-white shadow-sm text-[#1E3A5F]'
                                      : 'text-gray-700 hover:bg-white/60'
                                  }`}
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                  data-testid="mobile-menu-batiassist-accordion"
                                >
                                  <div className="flex items-center gap-3">
                                    <Icon size={20} className={batiAssistAccordionOpen || location.pathname.startsWith('/batiassist') ? 'text-[#F97316]' : 'text-gray-400'} />
                                    <span className="font-medium">{item.label}</span>
                                  </div>
                                  {batiAssistAccordionOpen ? (
                                    <ChevronUp size={18} className="text-gray-400" />
                                  ) : (
                                    <ChevronDown size={18} className="text-gray-400" />
                                  )}
                                </button>
                                
                                {/* Sous-items accordéon */}
                                <AnimatePresence>
                                  {batiAssistAccordionOpen && (
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: 'auto', opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: 0.2 }}
                                      className="overflow-hidden"
                                    >
                                      <div className="pl-4 mt-1 space-y-0.5 border-l-2 border-[#F97316]/30 ml-4">
                                        {item.subItems.map((subItem, subIndex) => {
                                          const SubIcon = subItem.icon;
                                          const isSubActive = location.pathname === subItem.path;
                                          return (
                                            <Link
                                              key={subIndex}
                                              to={subItem.path}
                                              onClick={() => setShowMobileMenu(false)}
                                              className={`flex items-center gap-3 px-3 min-h-[44px] rounded-xl transition-colors ${
                                                isSubActive
                                                  ? 'bg-[#F97316]/10 text-[#F97316]'
                                                  : 'text-gray-600 hover:bg-white/60'
                                              }`}
                                              style={{ WebkitTapHighlightColor: 'transparent' }}
                                            >
                                              <SubIcon size={16} className={isSubActive ? 'text-[#F97316]' : 'text-gray-400'} />
                                              <span className="text-sm font-medium">{subItem.label}</span>
                                            </Link>
                                          );
                                        })}
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            );
                          }
                          
                          // Items normaux - Touch target 44px
                          return (
                            <Link
                              key={index}
                              to={item.path}
                              onClick={() => setShowMobileMenu(false)}
                              className={`flex items-center gap-3 px-4 min-h-[44px] rounded-xl transition-colors ${
                                isActive
                                  ? 'bg-white shadow-sm text-[#1E3A5F]'
                                  : 'text-gray-700 hover:bg-white/60'
                              }`}
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <Icon size={20} className={isActive ? 'text-[#F97316]' : 'text-gray-400'} />
                              <span className="font-medium">{item.label}</span>
                            </Link>
                          );
                        })}
                      </nav>
                    </div>

                    {/* ══════ PARAMÈTRES (Version simplifiée pour Espace Pro) ══════ */}
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-1">
                        Paramètres
                      </p>
                      <nav className="space-y-1">
                        <button
                          onClick={toggleTheme}
                          className="flex items-center justify-between w-full px-3 py-3 rounded-xl text-gray-700 hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-3">
                            {isDarkMode ? (
                              <Sun size={20} className="text-[#F97316]" />
                            ) : (
                              <Moon size={20} className="text-gray-400" />
                            )}
                            <span className="font-medium">Mode {isDarkMode ? 'Clair' : 'Sombre'}</span>
                          </div>
                          <div className={`w-12 h-7 rounded-full p-1 transition-colors ${
                            isDarkMode ? 'bg-[#F97316]' : 'bg-gray-300'
                          }`}>
                            <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                              isDarkMode ? 'translate-x-5' : 'translate-x-0'
                            }`} />
                          </div>
                        </button>
                      </nav>
                    </div>
                  </>
                ) : (
                  /* ═══════════════════════════════════════════════════════════════
                      MENU STANDARD - Pour toutes les autres routes
                  ═══════════════════════════════════════════════════════════════ */
                  <>
                    {/* ══════ NAVIGATION PRINCIPALE ══════ */}
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-1">
                        Navigation
                      </p>
                      <nav className="space-y-1">
                        {[
                          { path: '/', label: 'Accueil', icon: Home },
                          { path: '/artisans', label: 'Artisans', icon: Users },
                          { path: '/produits', label: 'Marketplace', icon: Store },
                          { path: '/reseau', label: 'Réseau social', icon: Network },
                          { path: '/emploi', label: 'Emploi', icon: Briefcase },
                          { path: '/reseau-pros', label: 'Réseau Pros', icon: Building2 },
                          { path: '/pro', label: 'Espace Pro', icon: Crown },
                        ].map((item) => {
                          const Icon = item.icon;
                          const itemPath = '/' + (item.path.split('/')[1] || '');
                          const isActive = currentPath === itemPath;
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={() => setShowMobileMenu(false)}
                              className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                                isActive
                                  ? 'bg-[#1E40AF]/10 text-[#1E40AF]'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <Icon size={20} className={isActive ? 'text-[#1E40AF]' : 'text-gray-400'} />
                              <span className="font-medium">{item.label}</span>
                            </Link>
                          );
                        })}
                      </nav>
                    </div>

                    {/* ══════ SECTION PRO ══════ */}
                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-lg bg-[#F97316] flex items-center justify-center">
                          <Briefcase size={16} className="text-white" />
                        </div>
                        <p className="font-bold text-gray-900">Développer mon activité</p>
                      </div>
                      <nav className="space-y-1">
                        {PRO_SECTION.map((item, index) => {
                          const Icon = item.icon;
                          return (
                            <Link
                              key={index}
                              to={item.path}
                              onClick={() => setShowMobileMenu(false)}
                              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-700 hover:bg-white/80 transition-colors group"
                            >
                              <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shadow-sm group-hover:shadow transition-shadow">
                                <Icon size={18} className="text-[#1E40AF]" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 text-sm">{item.label}</p>
                                <p className="text-xs text-gray-500 truncate">{item.desc}</p>
                              </div>
                              <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-400 transition-colors" />
                            </Link>
                          );
                        })}
                      </nav>
                    </div>

                    {/* ══════ EXPLORATION ══════ */}
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-1">
                        Explorer
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {EXPLORE_SECTION.map((item, index) => {
                          const Icon = item.icon;
                          return (
                            <Link
                              key={index}
                              to={item.path}
                              onClick={() => setShowMobileMenu(false)}
                              className="flex items-center gap-2 px-3 py-3 bg-gray-50 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <Icon size={18} className="text-gray-400" />
                              <span className="text-sm font-medium">{item.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>

                    {/* ══════ PARAMÈTRES ══════ */}
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-1">
                        Paramètres
                      </p>
                      <nav className="space-y-1">
                        {/* Toggle Mode Sombre */}
                        <button
                          onClick={toggleTheme}
                          className="flex items-center justify-between w-full px-3 py-3 rounded-xl text-gray-700 hover:bg-gray-50 dark:hover:bg-slate-700"
                        >
                          <div className="flex items-center gap-3">
                            {isDarkMode ? (
                              <Sun size={20} className="text-[#F97316]" />
                            ) : (
                              <Moon size={20} className="text-gray-400" />
                            )}
                            <span className="font-medium">Mode {isDarkMode ? 'Clair' : 'Sombre'}</span>
                          </div>
                          <div className={`w-12 h-7 rounded-full p-1 transition-colors ${
                            isDarkMode ? 'bg-[#F97316]' : 'bg-gray-300'
                          }`}>
                            <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                              isDarkMode ? 'translate-x-5' : 'translate-x-0'
                            }`} />
                          </div>
                        </button>
                        
                        <Link
                          to="/mon-profil"
                          onClick={() => setShowMobileMenu(false)}
                          className="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 hover:bg-gray-50"
                        >
                          <User size={20} className="text-gray-400" />
                          <span className="font-medium">Profil</span>
                        </Link>
                        <Link
                          to="/parametres"
                          onClick={() => setShowMobileMenu(false)}
                          className="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 hover:bg-gray-50"
                        >
                          <Bell size={20} className="text-gray-400" />
                          <span className="font-medium">Notifications</span>
                        </Link>
                        <Link
                          to="/parametres"
                          onClick={() => setShowMobileMenu(false)}
                          className="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 hover:bg-gray-50"
                        >
                          <Shield size={20} className="text-gray-400" />
                          <span className="font-medium">Sécurité</span>
                        </Link>
                      </nav>
                    </div>
                  </>
                )}

                {/* ══════ DÉCONNEXION ══════ */}
                {isLoggedIn && (
                  <div className="pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        handleLogout();
                        setShowMobileMenu(false);
                      }}
                      className="flex items-center gap-3 px-3 py-3 rounded-xl text-red-600 hover:bg-red-50 w-full"
                    >
                      <LogOut size={20} />
                      <span className="font-medium">Déconnexion</span>
                    </button>
                  </div>
                )}

                {/* ══════ CONNEXION SI NON CONNECTÉ ══════ */}
                {!isLoggedIn && (
                  <div className="pt-4 border-t border-gray-200 space-y-3">
                    <Link 
                      to="/connexion"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <Button className="w-full h-12 bg-[#F97316] hover:bg-[#EA580C] rounded-xl font-semibold">
                        Activer ma boutique
                      </Button>
                    </Link>
                    <Link 
                      to="/connexion"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <Button variant="outline" className="w-full h-12 border-gray-300 rounded-xl font-medium">
                        J'ai déjà un compte
                      </Button>
                    </Link>
                  </div>
                )}

                {/* Spacer pour le safe area bottom */}
                <div className="h-8" />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Modal de publication (Desktop) */}
      <PublishModal 
        isOpen={showPublishModal} 
        onClose={() => setShowPublishModal(false)}
        isLoggedIn={isLoggedIn}
      />
    </>
  );
}
